import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import BuildIcon from "@mui/icons-material/Build";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CancelIcon from "@mui/icons-material/Cancel";
import EngineeringIcon from "@mui/icons-material/Engineering";
import PlumbingIcon from "@mui/icons-material/Plumbing";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import PestControlIcon from "@mui/icons-material/PestControl";
import ConstructionIcon from "@mui/icons-material/Construction";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { theme } from "../../theme/theme";
import {
  MaintenanceTicket,
  PostMaintenanceTicketsApiArg,
  Property,
  useGetMaintenanceTicketsQuery,
  usePostMaintenanceTicketsMutation,
  usePostMaintenanceTicketsUploadUrlMutation,
  usePatchMaintenanceTicketsByIdMutation,
} from "../../redux/residentPortalApi";
import { format } from "date-fns";

type MaintenanceTicketCategory = PostMaintenanceTicketsApiArg["body"]["category"];
type MaintenanceTicketPriority = NonNullable<PostMaintenanceTicketsApiArg["body"]["priority"]>;

const CATEGORIES = [
  {
    value: "plumbing",
    label: "Plumbing",
    icon: <PlumbingIcon fontSize="small" />,
  },
  {
    value: "electrical",
    label: "Electrical",
    icon: <ElectricBoltIcon fontSize="small" />,
  },
  {
    value: "structural",
    label: "Structural",
    icon: <ConstructionIcon fontSize="small" />,
  },
  {
    value: "appliance",
    label: "Appliance",
    icon: <HomeRepairServiceIcon fontSize="small" />,
  },
  {
    value: "pest-control",
    label: "Pest Control",
    icon: <PestControlIcon fontSize="small" />,
  },
  { value: "other", label: "Other", icon: <BuildIcon fontSize="small" /> },
];

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const useStyles = makeStyles({
  root: {
    padding: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 700,
    color: "#2c3e50",
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  formCard: {
    backgroundColor: "#f8f9fa",
    borderRadius: "12px",
    padding: theme.spacing(4),
    border: "1px solid #e8ecef",
    marginBottom: theme.spacing(4),
  },
  ticketCard: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    border: "1px solid #e8ecef",
    overflow: "hidden",
    marginBottom: theme.spacing(3),
  },
  ticketHeader: {
    padding: theme.spacing(3),
    borderBottom: "1px solid #f0f0f0",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  ticketBody: {
    padding: theme.spacing(3),
  },
  adminNotesBox: {
    backgroundColor: "#eaf6ff",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    border: "1px solid #aed6f1",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(8),
    color: "#7f8c8d",
  },
});

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<
    string,
    {
      label: string;
      color: "success" | "warning" | "error" | "default" | "info";
      icon: React.ReactElement;
    }
  > = {
    open: {
      label: "Open",
      color: "warning",
      icon: <HourglassEmptyIcon fontSize="small" />,
    },
    "in-progress": {
      label: "In Progress",
      color: "info",
      icon: <EngineeringIcon fontSize="small" />,
    },
    resolved: {
      label: "Resolved",
      color: "success",
      icon: <CheckCircleIcon fontSize="small" />,
    },
    closed: {
      label: "Closed",
      color: "default",
      icon: <CancelIcon fontSize="small" />,
    },
  };
  const s = map[status] ?? {
    label: status,
    color: "default" as any,
    icon: <></>,
  };
  return <Chip label={s.label} color={s.color} size="small" icon={s.icon} />;
};

const PriorityChip: React.FC<{ priority: string }> = ({ priority }) => {
  const map: Record<
    string,
    { label: string; color: "error" | "warning" | "info" | "default" }
  > = {
    urgent: { label: "Urgent", color: "error" },
    high: { label: "High", color: "warning" },
    medium: { label: "Medium", color: "info" },
    low: { label: "Low", color: "default" },
  };
  const p = map[priority] ?? { label: priority, color: "default" as any };
  return (
    <Chip label={p.label} color={p.color} size="small" variant="outlined" />
  );
};

const CategoryLabel: React.FC<{ category: string }> = ({ category }) => {
  const cat = CATEGORIES.find((c) => c.value === category);
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{ color: "#7f8c8d", fontSize: "0.875rem" }}
    >
      {cat?.icon}
      <span>{cat?.label ?? category}</span>
    </Stack>
  );
};

interface OwnProps {
  property: Property;
}

const isImage = (url: string) => /\.(jpe?g|png|webp|gif)(\?|$)/i.test(url);

const AttachmentList: React.FC<{
  urls: string[];
  onRemove?: (url: string) => void;
}> = ({ urls, onRemove }) => {
  if (!urls.length) return null;
  return (
    <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
      {urls.map((url) => (
        <Box
          key={url}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            border: "1px solid #e0e0e0",
            borderRadius: "6px",
            px: 1,
            py: 0.5,
            backgroundColor: "#fafafa",
            maxWidth: 220,
          }}
        >
          {isImage(url) ? (
            <Box
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Box
                component="img"
                src={url}
                alt="attachment"
                sx={{
                  width: 40,
                  height: 40,
                  objectFit: "cover",
                  borderRadius: "4px",
                  display: "block",
                }}
              />
            </Box>
          ) : (
            <Box
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#3498db",
                textDecoration: "none",
                fontSize: "0.78rem",
                overflow: "hidden",
              }}
            >
              <InsertDriveFileIcon fontSize="small" sx={{ flexShrink: 0 }} />
              <Typography
                noWrap
                sx={{ fontSize: "0.78rem", color: "#3498db", maxWidth: 130 }}
              >
                {decodeURIComponent(url.split("/").pop() ?? "file")}
              </Typography>
            </Box>
          )}
          {onRemove && (
            <Box
              component="span"
              onClick={() => onRemove(url)}
              sx={{
                cursor: "pointer",
                color: "#e74c3c",
                display: "flex",
                alignItems: "center",
                ml: 0.5,
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </Box>
          )}
        </Box>
      ))}
    </Stack>
  );
};

const MaintenanceTab: React.FC<OwnProps> = ({ property }) => {
  const classes = useStyles();

  const [showForm, setShowForm] = useState(false);
  const [formTitle, setFormTitle] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formCategory, setFormCategory] = useState("plumbing");
  const [formPriority, setFormPriority] = useState("medium");
  const [formAttachments, setFormAttachments] = useState<string[]>([]);
  const [formUploading, setFormUploading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Per-ticket update state
  const [editingTicketId, setEditingTicketId] = useState<string | null>(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [editAttachments, setEditAttachments] = useState<string[]>([]);
  const [editUploading, setEditUploading] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const {
    data: tickets,
    isFetching,
    refetch,
  } = useGetMaintenanceTicketsQuery(
    { propertyId: property.id ?? "" },
    { skip: !property.id },
  );

  const [postTicket, { isLoading: isSubmitting }] =
    usePostMaintenanceTicketsMutation();

  const [patchTicket, { isLoading: isUpdating }] =
    usePatchMaintenanceTicketsByIdMutation();

  const [getUploadUrl] = usePostMaintenanceTicketsUploadUrlMutation();

  const uploadFiles = async (
    files: FileList,
    setUploading: (v: boolean) => void,
    setAttachments: React.Dispatch<React.SetStateAction<string[]>>,
    setError: (v: string | null) => void,
  ) => {
    setUploading(true);
    setError(null);
    try {
      const urls: string[] = [];
      for (const file of Array.from(files)) {
        const { uploadUrl, fileUrl } = await getUploadUrl({
          body: { filename: file.name, contentType: file.type },
        }).unwrap();
        if (!uploadUrl || !fileUrl) {
          throw new Error("Upload URL was not returned by the server.");
        }
        await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });
        urls.push(fileUrl);
      }
      setAttachments((prev) => [...prev, ...urls]);
    } catch {
      setError("Failed to upload file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const openEdit = (ticket: MaintenanceTicket) => {
    setEditingTicketId(ticket.id ?? null);
    setEditNoteText(ticket.residentNotes ?? "");
    setEditAttachments(ticket.attachments ?? []);
    setUpdateError(null);
  };

  const cancelEdit = () => {
    setEditingTicketId(null);
    setEditNoteText("");
    setEditAttachments([]);
    setUpdateError(null);
  };

  const handleSaveNote = async (ticketId: string) => {
    setUpdateError(null);
    try {
      await patchTicket({
        id: ticketId,
        body: {
          residentNotes: editNoteText.trim(),
          attachments: editAttachments,
        },
      }).unwrap();
      setEditingTicketId(null);
      setEditNoteText("");
      setEditAttachments([]);
      refetch();
    } catch (err: any) {
      setUpdateError(
        err?.data?.error?.message ?? "Failed to save update. Please try again.",
      );
    }
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!formTitle.trim() || !formDescription.trim()) {
      setSubmitError("Please fill in both title and description.");
      return;
    }
    try {
      await postTicket({
        body: {
          propertyId: property.id ?? "",
          title: formTitle.trim(),
          description: formDescription.trim(),
          category: formCategory as MaintenanceTicketCategory,
          priority: formPriority as MaintenanceTicketPriority,
          attachments: formAttachments,
        },
      }).unwrap();
      setSubmitSuccess(true);
      setFormTitle("");
      setFormDescription("");
      setFormCategory("plumbing");
      setFormPriority("medium");
      setFormAttachments([]);
      setShowForm(false);
      refetch();
    } catch (err: any) {
      setSubmitError(
        err?.data?.error?.message ??
          "Failed to submit ticket. Please try again.",
      );
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setSubmitError(null);
    setFormTitle("");
    setFormDescription("");
    setFormCategory("plumbing");
    setFormPriority("medium");
    setFormAttachments([]);
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" className={classes.sectionTitle}>
          <BuildIcon sx={{ color: "#3498db" }} />
          Maintenance Requests
        </Typography>
        {!showForm && (
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => {
              setShowForm(true);
              setSubmitSuccess(false);
            }}
            sx={{
              backgroundColor: "#3498db",
              "&:hover": { backgroundColor: "#2980b9" },
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Log Issue
          </Button>
        )}
      </Box>

      {submitSuccess && (
        <Alert
          severity="success"
          sx={{ mb: 3, borderRadius: "8px" }}
          onClose={() => setSubmitSuccess(false)}
        >
          Your maintenance request has been submitted successfully. Our team
          will be in touch.
        </Alert>
      )}

      {/* Submit Form */}
      {showForm && (
        <Paper className={classes.formCard} elevation={0}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#2c3e50", mb: 2 }}
          >
            Log a New Maintenance Issue
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Issue Title"
                placeholder="e.g. Leaking tap in bathroom"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                fullWidth
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Category</InputLabel>
                <Select
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value)}
                  label="Category"
                >
                  {CATEGORIES.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {cat.icon}
                        <span>{cat.label}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formPriority}
                  onChange={(e) => setFormPriority(e.target.value)}
                  label="Priority"
                >
                  {PRIORITIES.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                placeholder="Please describe the issue in detail..."
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                fullWidth
                required
                multiline
                rows={4}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Button
                  component="label"
                  variant="outlined"
                  size="small"
                  startIcon={
                    formUploading ? (
                      <CircularProgress size={14} />
                    ) : (
                      <AttachFileIcon />
                    )
                  }
                  disabled={formUploading}
                  sx={{ textTransform: "none", borderRadius: "6px" }}
                >
                  {formUploading ? "Uploading…" : "Attach Documents / Photos"}
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/*,application/pdf,.doc,.docx"
                    onChange={(e) => {
                      if (e.target.files) {
                        uploadFiles(
                          e.target.files,
                          setFormUploading,
                          setFormAttachments,
                          setSubmitError,
                        );
                        e.target.value = "";
                      }
                    }}
                  />
                </Button>
                <AttachmentList
                  urls={formAttachments}
                  onRemove={(url) =>
                    setFormAttachments((prev) => prev.filter((u) => u !== url))
                  }
                />
              </Box>
            </Grid>
          </Grid>

          {submitError && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: "8px" }}>
              {submitError}
            </Alert>
          )}

          <Stack direction="row" spacing={2} mt={3}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              sx={{
                backgroundColor: "#3498db",
                "&:hover": { backgroundColor: "#2980b9" },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancelForm}
              disabled={isSubmitting}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Tickets List */}
      <Box>
        <Typography variant="subtitle1" sx={{ color: "#7f8c8d", mb: 2 }}>
          Your Submitted Requests
        </Typography>

        {isFetching ? (
          <Stack spacing={2}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={100} />
            ))}
          </Stack>
        ) : !tickets || tickets.length === 0 ? (
          <Paper
            elevation={0}
            sx={{ border: "1px solid #e8ecef", borderRadius: "12px" }}
          >
            <Box className={classes.emptyState}>
              <BuildIcon sx={{ fontSize: 64, color: "#bdc3c7", mb: 2 }} />
              <Typography variant="h6" sx={{ color: "#7f8c8d" }}>
                No maintenance requests yet
              </Typography>
              <Typography variant="body2" sx={{ color: "#95a5a6", mt: 1 }}>
                Use the "Log Issue" button above to submit a maintenance
                request.
              </Typography>
            </Box>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {tickets.map((ticket: MaintenanceTicket) => (
              <Paper
                key={ticket.id}
                className={classes.ticketCard}
                elevation={0}
              >
                <Box className={classes.ticketHeader}>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#2c3e50",
                        fontSize: "1rem",
                      }}
                    >
                      {ticket.title}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      mt={0.5}
                      alignItems="center"
                    >
                      <CategoryLabel category={ticket.category} />
                      <Typography sx={{ color: "#bdc3c7" }}>·</Typography>
                      <Typography sx={{ color: "#95a5a6", fontSize: "0.8rem" }}>
                        {ticket.createdAt
                          ? format(new Date(ticket.createdAt), "dd MMM yyyy")
                          : "—"}
                      </Typography>
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PriorityChip priority={ticket.priority} />
                    <StatusChip status={ticket.status} />
                  </Stack>
                </Box>
                <Box className={classes.ticketBody}>
                  <Typography
                    sx={{ color: "#555", fontSize: "0.95rem", lineHeight: 1.6 }}
                  >
                    {ticket.description}
                  </Typography>

                  {/* Ticket attachments */}
                  {ticket.attachments && ticket.attachments.length > 0 && (
                    <Box mt={1.5}>
                      <Typography
                        sx={{
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          color: "#7f8c8d",
                          mb: 0.5,
                        }}
                      >
                        Attachments:
                      </Typography>
                      <AttachmentList urls={ticket.attachments} />
                    </Box>
                  )}

                  {ticket.adminNotes && (
                    <Box className={classes.adminNotesBox}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#2e86c1",
                          fontSize: "0.85rem",
                          mb: 0.5,
                        }}
                      >
                        Update from Management:
                      </Typography>
                      <Typography sx={{ color: "#2c3e50", fontSize: "0.9rem" }}>
                        {ticket.adminNotes}
                      </Typography>
                    </Box>
                  )}

                  {/* Resident notes display */}
                  {ticket.residentNotes && editingTicketId !== ticket.id && (
                    <Box
                      sx={{
                        backgroundColor: "#f0fdf4",
                        borderRadius: "8px",
                        p: 2,
                        mt: 2,
                        border: "1px solid #86efac",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#16a34a",
                          fontSize: "0.85rem",
                          mb: 0.5,
                        }}
                      >
                        Your Update:
                      </Typography>
                      <Typography sx={{ color: "#2c3e50", fontSize: "0.9rem" }}>
                        {ticket.residentNotes}
                      </Typography>
                    </Box>
                  )}

                  {/* Inline edit area */}
                  {editingTicketId === ticket.id ? (
                    <Box sx={{ mt: 2 }}>
                      <TextField
                        label="Add / Edit Your Update"
                        value={editNoteText}
                        onChange={(e) => setEditNoteText(e.target.value)}
                        multiline
                        rows={3}
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Provide additional context or a status update..."
                      />
                      <Box mt={1.5}>
                        <Button
                          component="label"
                          variant="outlined"
                          size="small"
                          startIcon={
                            editUploading ? (
                              <CircularProgress size={14} />
                            ) : (
                              <AttachFileIcon />
                            )
                          }
                          disabled={editUploading}
                          sx={{ textTransform: "none", borderRadius: "6px" }}
                        >
                          {editUploading ? "Uploading…" : "Attach Files"}
                          <input
                            type="file"
                            hidden
                            multiple
                            accept="image/*,application/pdf,.doc,.docx"
                            onChange={(e) => {
                              if (e.target.files) {
                                uploadFiles(
                                  e.target.files,
                                  setEditUploading,
                                  setEditAttachments,
                                  setUpdateError,
                                );
                                e.target.value = "";
                              }
                            }}
                          />
                        </Button>
                        <AttachmentList
                          urls={editAttachments}
                          onRemove={(url) =>
                            setEditAttachments((prev) =>
                              prev.filter((u) => u !== url),
                            )
                          }
                        />
                      </Box>
                      {updateError && (
                        <Alert
                          severity="error"
                          sx={{ mt: 1, borderRadius: "8px" }}
                        >
                          {updateError}
                        </Alert>
                      )}
                      <Stack direction="row" spacing={1} mt={1.5}>
                        <Button
                          variant="contained"
                          size="small"
                          disabled={isUpdating || editUploading}
                          onClick={() => handleSaveNote(ticket.id!)}
                          startIcon={
                            isUpdating ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : undefined
                          }
                          sx={{
                            backgroundColor: "#27ae60",
                            "&:hover": { backgroundColor: "#1e8449" },
                            textTransform: "none",
                            borderRadius: "6px",
                          }}
                        >
                          {isUpdating ? "Saving..." : "Save Update"}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isUpdating}
                          onClick={cancelEdit}
                          sx={{ textTransform: "none", borderRadius: "6px" }}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Box>
                  ) : (
                    ticket.status !== "closed" && (
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => openEdit(ticket)}
                          sx={{
                            textTransform: "none",
                            borderRadius: "6px",
                            fontSize: "0.8rem",
                            color: "#27ae60",
                            borderColor: "#27ae60",
                            "&:hover": {
                              borderColor: "#1e8449",
                              backgroundColor: "#f0fdf4",
                            },
                          }}
                        >
                          {ticket.residentNotes ? "Edit Update" : "Add Update"}
                        </Button>
                      </Box>
                    )
                  )}
                </Box>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default MaintenanceTab;
